import { FormEvent, useCallback, useEffect, useState } from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";

import styles from "Pages/Setup/Configuration/Components/ConfigModal/ConfigModal.module.css";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";

type ActionType = "new" | "edit" | "delete" | "";

interface IProps {
    activeModal: ActionType;
    updateConfigs: (data: { keyName: string; keyValue: string }) => void;
    onSetActiveModal: (modal: ActionType) => void;
    onSetSelectedRow: (row: any) => void;
    selectedRow: any;
    /** Optional: pre-fill (and lock) the key field when creating a new entry.
     * Used by guided Connect pages so the user only supplies the value. */
    prefillKey?: string;
}

export default function EditModal({
    activeModal,
    updateConfigs,
    onSetActiveModal,
    onSetSelectedRow,
    selectedRow,
    prefillKey,
}: IProps) {
    const [formData, setFormData] = useState({ key: "", value: "" });

    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();

    const { handleFormChange } = useHelpers();
    const { post } = useFetch();

    const closeModal = useCallback(() => {
        onSetActiveModal("");
        setFormData({ key: "", value: "" });
    }, [onSetActiveModal]);

    // Handle alert for no row selected, prefilling inputs conditionally
    useEffect(() => {
        if (activeModal === "new")
            setFormData({ key: prefillKey ?? "", value: "" });

        // Handle no row selected for edit and delete
        if (
            (activeModal === "edit" || activeModal === "delete") &&
            !selectedRow
        ) {
            closeModal();
            newAlert({
                message: "No entry selected",
                color: "danger",
            });
            return;
        }

        // Set formData to selected row values so values appear in inputs
        if (activeModal !== "new") {
            setFormData({
                key: selectedRow ? selectedRow.values.keyName : "",
                value: selectedRow ? selectedRow.values.keyValue : "",
            });
        }
    }, [activeModal, closeModal, newAlert, selectedRow, prefillKey]);

    async function submitFormData(e: FormEvent) {
        e.preventDefault();

        const url = `/api/configuration/${activeModal}/${userContext.companyId}`;

        const body = {
            keyName: `${formData.key}`,
            keyValue: formData.value,
        };

        const response = await post(url, body);
        const data = await response.json();
        console.log(data);

        closeModal();

        if (response.ok) {
            // Show the alert
            newAlert({
                isOpen: true,
                message: "Request submitted successfully",
                color: "success",
            });
            // Erase the selected row state
            onSetSelectedRow(undefined);
            updateConfigs(data);
        } else {
            newAlert({
                isOpen: true,
                message: `Error, request failed`,
                color: "danger",
            });
        }
    }

    let headerContent = "";
    switch (activeModal) {
        case "new":
            headerContent = "Create new entry";
            break;
        case "edit":
            headerContent = "Edit entry";
            break;
        case "delete":
            headerContent = "Delete entry";

            break;
    }

    const bodyContent = (
        <Form id="form" onSubmit={submitFormData}>
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="key">Key: </Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="key"
                        name="key"
                        className={styles.input}
                        minLength={3}
                        maxLength={100}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.key}
                        disabled={
                            activeModal === "edit" ||
                            activeModal === "delete" ||
                            (activeModal === "new" && !!prefillKey)
                        }
                    />
                </Col>
            </FormGroup>
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="value">Value:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="value"
                        name="value"
                        className={styles.input}
                        minLength={1}
                        maxLength={4096}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.value}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>
        </Form>
    );

    return (
        <Modal isOpen={activeModal !== ""} toggle={closeModal}>
            <ModalHeader toggle={closeModal}>{headerContent}</ModalHeader>
            <ModalBody>{bodyContent}</ModalBody>
            <ModalFooter>
                <Button
                    color="primary"
                    type="submit"
                    form="form"
                    disabled={formData?.value === selectedRow?.values.keyValue}
                >
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    );
}
